import {
    compareAccountsByLeafGroup,
    getPredefinedGroupLabel,
    orderedPredefinedLeafTypes,
} from "@common/constants/domains/accounting/accountGroups";
import { AccountType } from "@common/constants/domains/accounting/accounts";
import {
    compareScopeOrderTiebreak,
    ScopeMemberKind,
} from "@common/helpers/domains/accounting/chartScopeOrdering";
import { DraftAccount, DraftGroup } from "../draftState";
import { resolveCustomGroupChain } from "../groupChain";
import { NestedEditorRow, NestedRowKind } from "./nestedRowTypes";
import {
    pruneFilteredNestedTypeRows,
    wrapNestedTypeRowsInSections,
} from "./nestedSectionWrapping";

interface BuildNestedRowsParams {
    accounts: DraftAccount[];
    groups: DraftGroup[];
    hasActiveFilter?: boolean;
}

// Key headers by exact type; report-leaf folding is presentation-only.
export const getTypeHeaderRowId = (type: AccountType): string => `type:${type}`;

interface MutableNode {
    id: string;
    kind: NestedRowKind;
    name: string;
    depth: number;
    insertionIndex: number;
    sortOrder: number;
    children: MutableNode[];
    account?: DraftAccount;
    group?: DraftGroup;
    accountType?: AccountType;
}

const isLeafItem = (kind: NestedRowKind): boolean =>
    kind === "account" || kind === "group";

interface LeafSibling {
    id: string;
    sortOrder: number;
    kind: NestedRowKind;
    name: string;
}

const toScopeOrderMember = (
    row: LeafSibling,
): { id: string; kind: ScopeMemberKind; name: string } => ({
    id: row.id,
    kind: row.kind === "account" ? "account" : "group",
    name: row.name,
});

export const compareLeafSiblings = (a: LeafSibling, b: LeafSibling): number => {
    if (a.sortOrder !== b.sortOrder) {
        return a.sortOrder - b.sortOrder;
    }
    return compareScopeOrderTiebreak(
        toScopeOrderMember(a),
        toScopeOrderMember(b),
    );
};

const compareNodes = (a: MutableNode, b: MutableNode): number => {
    if (isLeafItem(a.kind) && isLeafItem(b.kind)) {
        return compareLeafSiblings(a, b);
    }
    return a.insertionIndex - b.insertionIndex;
};

const toNestedRow = (node: MutableNode): NestedEditorRow => {
    const children =
        node.children.length > 0
            ? [...node.children].sort(compareNodes).map(toNestedRow)
            : undefined;
    return {
        id: node.id,
        kind: node.kind,
        name: node.name,
        depth: node.depth,
        children,
        account: node.account,
        group: node.group,
        accountType: node.accountType,
        accounts: node.account ? [node.account] : [],
        groups: node.group ? [node.group] : [],
    };
};

export const buildNestedRows = ({
    accounts,
    groups,
    hasActiveFilter = false,
}: BuildNestedRowsParams): NestedEditorRow[] => {
    const groupsById = new Map(groups.map((group) => [group.id, group]));
    const nodeByPathKey = new Map<string, MutableNode>();
    const rootChildren: MutableNode[] = [];
    let insertionCounter = 0;

    const getOrCreate = (
        siblings: MutableNode[],
        pathKey: string,
        create: () => Omit<MutableNode, "insertionIndex" | "children">,
    ): MutableNode => {
        const existing = nodeByPathKey.get(pathKey);
        if (existing) {
            return existing;
        }
        const node: MutableNode = {
            ...create(),
            insertionIndex: insertionCounter++,
            children: [],
        };
        nodeByPathKey.set(pathKey, node);
        siblings.push(node);
        return node;
    };

    const ensureTypeHeader = (
        type: AccountType,
    ): { siblings: MutableNode[]; depth: number } => {
        const headerKey = `type:${type}`;
        const node = getOrCreate(rootChildren, headerKey, () => ({
            id: headerKey,
            kind: "type",
            name: getPredefinedGroupLabel(type),
            depth: 0,
            sortOrder: 0,
            accountType: type,
        }));
        return { siblings: node.children, depth: 1 };
    };

    const ensureGroupChain = (
        group: DraftGroup,
    ): { siblings: MutableNode[]; depth: number } => {
        const ancestors = resolveCustomGroupChain(
            group.parentGroupId,
            groupsById,
        )
            .ids.map((id) => groupsById.get(id))
            .filter((ancestor): ancestor is DraftGroup => ancestor != null);
        let { siblings, depth } = ensureTypeHeader(group.type);
        for (const node of [...ancestors, group]) {
            const groupKey = `group:${node.id}`;
            const currentDepth = depth;
            const groupNode = getOrCreate(siblings, groupKey, () => ({
                id: node.id,
                kind: "group",
                name: node.name,
                depth: currentDepth,
                sortOrder: node.sortOrder,
                group: node,
                accountType: node.type,
            }));
            siblings = groupNode.children;
            depth += 1;
        }
        return { siblings, depth };
    };

    // Pre-create canonical exact-type headers so processing order cannot reorder them.
    const presentTypes = new Set<AccountType>();
    for (const account of accounts) {
        presentTypes.add(account.type);
    }
    for (const group of groups) {
        presentTypes.add(group.type);
    }
    for (const leafType of orderedPredefinedLeafTypes) {
        if (presentTypes.has(leafType)) {
            ensureTypeHeader(leafType);
        }
    }

    // Project all custom groups (including empty ones) before accounts.
    for (const group of groups) {
        ensureGroupChain(group);
    }

    const orderedAccounts = [...accounts].sort(compareAccountsByLeafGroup);
    for (const account of orderedAccounts) {
        const parentGroup =
            account.parentGroupId != null
                ? groupsById.get(account.parentGroupId)
                : undefined;
        const { siblings, depth } = parentGroup
            ? ensureGroupChain(parentGroup)
            : ensureTypeHeader(account.type);
        siblings.push({
            id: account.id,
            kind: "account",
            name: account.name,
            depth,
            insertionIndex: insertionCounter++,
            sortOrder: account.sortOrder,
            children: [],
            account,
            accountType: account.type,
        });
    }

    const typeRows = [...rootChildren].sort(compareNodes).map(toNestedRow);
    const prunedTypeRows = hasActiveFilter
        ? pruneFilteredNestedTypeRows(typeRows)
        : typeRows;
    return wrapNestedTypeRowsInSections(prunedTypeRows);
};
